import {
  RaidPlanActor,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import { RaidPlanTimelinePatch } from 'app/frontend/base/api/raid_plans'

export type { RaidPlanTimelinePatch }

export type TimelineDiff =
  | { kind: 'noop' }
  | { kind: 'patch'; patch: RaidPlanTimelinePatch }
  | { kind: 'replace' }

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const definedKeyCount = (value: Record<string, unknown>) =>
  Object.keys(value).reduce(
    (count, key) => (value[key] === undefined ? count : count + 1),
    0,
  )

// Key order differs between locally mutated objects and values round-tripped
// through jsonb, so equality has to ignore it. An undefined value and a missing
// key also have to count as equal: the editor assigns undefined to clear a
// field, JSON drops it on the way to the server, and treating the two as
// different would leave the timeline permanently dirty and saving in a loop.
const deepEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true
  if (Array.isArray(left)) {
    if (!Array.isArray(right) || left.length !== right.length) return false
    return left.every((item, index) => deepEqual(item, right[index]))
  }
  if (isPlainObject(left)) {
    if (!isPlainObject(right)) return false
    if (definedKeyCount(left) !== definedKeyCount(right)) return false
    return Object.keys(left).every(
      (key) => left[key] === undefined || deepEqual(left[key], right[key]),
    )
  }
  return false
}

type DecomposedTimeline = {
  skeleton: RaidPlanTimeline
  actorOrder: string[]
  actors: Map<string, RaidPlanActor>
  abilities: Map<string, RaidPlanEncounterAssignmentAbility>
}

// Actors and assignment abilities are split out of the document so that two
// editors working on different rows never overwrite each other. Everything
// else (phases, general casts, ability order) travels together as the skeleton.
const decompose = (timeline: RaidPlanTimeline): DecomposedTimeline | null => {
  const actors = new Map<string, RaidPlanActor>()
  const actorOrder: string[] = []

  for (const actor of timeline.actors ?? []) {
    const uuid = actor?.uuid
    if (typeof uuid !== 'string' || uuid === '' || actors.has(uuid)) return null
    actors.set(uuid, actor)
    actorOrder.push(uuid)
  }

  const assignments = timeline.encounter_assignments
  const abilities = new Map(Object.entries(assignments?.abilities ?? {}))

  const skeleton: RaidPlanTimeline = { ...timeline, actors: [] }
  if (assignments) {
    skeleton.encounter_assignments = { ...assignments, abilities: {} }
  }

  return { skeleton, actorOrder, actors, abilities }
}

const collectUpserts = <T, R>(
  base: Map<string, T>,
  next: Map<string, T>,
  build: (key: string, value: T) => R,
) => {
  const upserts: R[] = []
  next.forEach((value, key) => {
    if (base.has(key) && deepEqual(base.get(key), value)) return
    upserts.push(build(key, clone(value)))
  })
  return upserts.length > 0 ? upserts : null
}

const collectRemovals = <T>(base: Map<string, T>, next: Map<string, T>) => {
  const removed: string[] = []
  base.forEach((_value, key) => {
    if (!next.has(key)) removed.push(key)
  })
  return removed.length > 0 ? removed : null
}

export const diffTimeline = (
  base: RaidPlanTimeline,
  next: RaidPlanTimeline,
): TimelineDiff => {
  const baseParts = decompose(base)
  const nextParts = decompose(next)
  if (!baseParts || !nextParts) return { kind: 'replace' }

  const patch: RaidPlanTimelinePatch = {}

  if (!deepEqual(baseParts.skeleton, nextParts.skeleton)) {
    patch.skeleton = clone(nextParts.skeleton)
  }
  if (!deepEqual(baseParts.actorOrder, nextParts.actorOrder)) {
    patch.actor_order = [...nextParts.actorOrder]
  }

  const actors = collectUpserts(
    baseParts.actors,
    nextParts.actors,
    (_uuid, actor) => actor,
  )
  if (actors) patch.actors = actors
  const removedActors = collectRemovals(baseParts.actors, nextParts.actors)
  if (removedActors) patch.removed_actor_uuids = removedActors

  const abilities = collectUpserts(
    baseParts.abilities,
    nextParts.abilities,
    (key, ability) => ({ key, ability }),
  )
  if (abilities) patch.abilities = abilities
  const removedAbilities = collectRemovals(
    baseParts.abilities,
    nextParts.abilities,
  )
  if (removedAbilities) patch.removed_ability_keys = removedAbilities

  if (Object.keys(patch).length === 0) return { kind: 'noop' }
  return { kind: 'patch', patch }
}

const orderedKeys = <T>(order: string[], entries: Map<string, T>) => {
  const seen = new Set<string>()
  const result: string[] = []
  order.forEach((key) => {
    if (seen.has(key) || !entries.has(key)) return
    seen.add(key)
    result.push(key)
  })
  entries.forEach((_value, key) => {
    if (seen.has(key)) return
    seen.add(key)
    result.push(key)
  })
  return result
}

export const applyTimelinePatch = (
  timeline: RaidPlanTimeline,
  patch: RaidPlanTimelinePatch,
): RaidPlanTimeline => {
  const parts = decompose(timeline)
  if (!parts) {
    throw new Error('Cannot patch a timeline with missing or duplicate actors')
  }

  const { actors, abilities } = parts
  patch.removed_actor_uuids?.forEach((uuid) => actors.delete(uuid))
  patch.actors?.forEach((actor) => {
    if (typeof actor?.uuid !== 'string' || actor.uuid === '') {
      throw new Error('Every patched actor needs a uuid')
    }
    actors.set(actor.uuid, actor)
  })
  patch.removed_ability_keys?.forEach((key) => abilities.delete(key))
  patch.abilities?.forEach(({ key, ability }) => abilities.set(key, ability))

  const result = clone(patch.skeleton ?? parts.skeleton)
  result.actors = orderedKeys(
    patch.actor_order ?? parts.actorOrder,
    actors,
  ).map((uuid) => clone(actors.get(uuid) as RaidPlanActor))

  if (abilities.size > 0 || result.encounter_assignments) {
    result.encounter_assignments = {
      ...result.encounter_assignments,
      abilities: Object.fromEntries(
        Array.from(abilities, ([key, ability]) => [key, clone(ability)]),
      ),
    }
  }

  return result
}

export const rebaseLocalTimeline = ({
  localTimeline,
  baseTimeline,
  remoteTimeline,
}: {
  localTimeline: RaidPlanTimeline
  baseTimeline: RaidPlanTimeline
  remoteTimeline: RaidPlanTimeline
}) => {
  // Derived from the timelines themselves rather than from the diff, so that a
  // timeline we cannot build a patch for is not reported as dirty forever.
  if (deepEqual(baseTimeline, localTimeline)) {
    return { timeline: remoteTimeline, hasLocalDelta: false }
  }

  const diff = diffTimeline(baseTimeline, localTimeline)
  if (diff.kind === 'patch') {
    try {
      return {
        timeline: applyTimelinePatch(remoteTimeline, diff.patch),
        hasLocalDelta: true,
      }
    } catch {
      // Keeping the local edits is always safer than dropping them.
    }
  }
  return { timeline: localTimeline, hasLocalDelta: true }
}
